import React, { useRef, useMemo, forwardRef, useImperativeHandle } from 'react';
import PropTypes from 'prop-types';
import { toCanvas } from 'html-to-image';
import { formatDistance } from '../../utils/gpx';
import { roadTripDims, buildStaticMapUrl, fetchStaticMapImage } from '../../utils/places';
import { PALETTE, BORDERS, FONT_FAMILY } from '../../theme';

/**
 * Road Trips render — the drive drawn ON Google's basemap (the user's explicit
 * choice). The route + numbered stop markers are baked onto the map by the
 * Google Maps Static API; our title / stats cards sit over it as DOM overlays.
 *
 * PREVIEW uses a direct Static Maps <img> (the browser key works — <img>
 * requests carry a Referer). EXPORT can't read Google's cross-origin pixels
 * into a canvas, so capture() pulls the printable image from the Cloud Function
 * (`fetchStaticMapImage`, server-side fetch → our-origin data URL), draws it to
 * a canvas, and composites the title/stats chrome on top.
 *
 * NOTE: stop NAMES aren't placed on the map in Google mode (the Static API
 * can't text-label arbitrary points, and it auto-fits so we can't project) —
 * markers are numbered, and the numbered list in the page maps number → name.
 */

const loadImage = (src) => new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
});

const RoadTripRender = forwardRef(
    ({ stops = [], polyline = null, maptype = 'roadmap', colorHex = '0xe8632d', title = 'My Road Trip', units = 'metric', show = { title: true, stats: true }, statFields = { stops: true, distance: true }, distanceM = null, source = 'roads' }, ref) => {
        const cardRef = useRef(null);
        const mapAreaRef = useRef(null);
        const imgRef = useRef(null);

        const { width, height } = roadTripDims(stops);

        const previewUrl = useMemo(
            () => (stops.length ? buildStaticMapUrl({ polyline, stops, maptype, width, height, scale: 2, color: colorHex }) : null),
            [polyline, stops, maptype, width, height, colorHex]
        );

        const statItems = [
            statFields.stops ? { l: 'Stops', v: String(stops.length) } : null,
            statFields.distance && distanceM != null ? { l: 'Distance', v: formatDistance(distanceM, units) } : null,
        ].filter(Boolean);

        useImperativeHandle(ref, () => ({
            async capture() {
                const cardEl = cardRef.current;
                const mapAreaEl = mapAreaRef.current;
                if (!cardEl || !mapAreaEl || !stops.length) throw new Error('Not ready');
                // 1. Printable basemap from the Cloud Function (server-side fetch →
                //    same-origin data URL, so it can be drawn to a canvas).
                const dataUrl = await fetchStaticMapImage({ polyline, stops, maptype, width, height, scale: 2, color: colorHex });
                const baseImg = await loadImage(dataUrl);
                // 2. Composite at the basemap's native resolution.
                const cardRect = cardEl.getBoundingClientRect();
                const mapRect = mapAreaEl.getBoundingClientRect();
                const S = baseImg.width / mapRect.width; // px-per-CSS-px of the basemap
                const out = document.createElement('canvas');
                out.width = Math.round(cardRect.width * S);
                out.height = Math.round(cardRect.height * S);
                const ctx = out.getContext('2d');
                ctx.fillStyle = PALETTE.surface;
                ctx.fillRect(0, 0, out.width, out.height);
                ctx.drawImage(baseImg, Math.round((mapRect.left - cardRect.left) * S), Math.round((mapRect.top - cardRect.top) * S), Math.round(mapRect.width * S), Math.round(mapRect.height * S));
                // 3. Title / stats chrome (the preview <img> filtered out, transparent bg).
                const chrome = await toCanvas(cardEl, {
                    pixelRatio: S,
                    skipFonts: true,
                    backgroundColor: undefined,
                    style: { background: 'transparent' },
                    filter: (node) => !(node.classList && node.classList.contains('rt-static-img')),
                });
                ctx.drawImage(chrome, 0, 0, out.width, out.height);
                return await new Promise((r) => out.toBlob(r, 'image/png'));
            },
        }), [polyline, stops, maptype, width, height, colorHex]);

        const subtitleBits = [
            statFields.stops && stops.length ? `${stops.length} stops` : null,
            statFields.distance && distanceM != null ? formatDistance(distanceM, units) : null,
        ].filter(Boolean);
        const overlayCard = { background: 'rgba(255,255,255,0.93)', borderRadius: 10, boxShadow: '0 2px 10px rgba(0,0,0,0.15)' };

        return (
            <div style={{ width: '100%', maxWidth: 560, margin: '0 auto' }}>
              <div ref={cardRef} style={{ width: '100%', background: PALETTE.surface, borderRadius: 16, overflow: 'hidden', border: BORDERS.faint, fontFamily: FONT_FAMILY }}>
                <div ref={mapAreaRef} style={{ position: 'relative', width: '100%', aspectRatio: `${width} / ${height}` }}>
                    {previewUrl && (
                        <img ref={imgRef} className="rt-static-img" src={previewUrl} alt="Road trip map"
                            style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
                    )}

                    {show.title && (
                        <div style={{ ...overlayCard, position: 'absolute', top: 12, left: '50%', transform: 'translateX(-50%)', maxWidth: '88%', padding: '8px 16px', textAlign: 'center', zIndex: 3 }}>
                            {title && <div style={{ fontWeight: 800, fontSize: 21, letterSpacing: '-0.01em', color: PALETTE.text, lineHeight: 1.15 }}>{title}</div>}
                            {subtitleBits.length > 0 && <div style={{ fontWeight: 600, fontSize: 12.5, color: PALETTE.textMuted, marginTop: 2 }}>{subtitleBits.join(' · ')}</div>}
                        </div>
                    )}

                    {show.stats && statItems.length > 0 && (
                        <div style={{ ...overlayCard, position: 'absolute', left: 12, right: 12, bottom: 12, padding: '8px 6px', display: 'flex', justifyContent: 'space-around', gap: 4, zIndex: 3 }}>
                            {statItems.map((s) => (
                                <div key={s.l} style={{ textAlign: 'center' }}>
                                    <div style={{ fontWeight: 800, fontSize: 17, color: PALETTE.text, whiteSpace: 'nowrap' }}>{s.v}</div>
                                    <div style={{ fontWeight: 700, fontSize: 9, letterSpacing: '0.03em', color: PALETTE.textMuted, marginTop: 2, whiteSpace: 'nowrap' }}>{s.l.toUpperCase()}</div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
              </div>
              {/* Google's own attribution is baked into the static image, so we
                  don't repeat it here. Only surface the fallback note when the
                  route is the straight-line approximation. */}
              {source === 'straight' && (
                <div style={{ fontSize: 12, color: PALETTE.textMuted, textAlign: 'center', marginTop: 10, fontFamily: FONT_FAMILY, lineHeight: 1.4 }}>
                  Approximate route
                </div>
              )}
            </div>
        );
    }
);

RoadTripRender.displayName = 'RoadTripRender';
RoadTripRender.propTypes = {
    stops: PropTypes.array,
    polyline: PropTypes.string,
    maptype: PropTypes.string,
    colorHex: PropTypes.string,
    title: PropTypes.string,
    units: PropTypes.oneOf(['metric', 'imperial']),
    show: PropTypes.object,
    statFields: PropTypes.object,
    distanceM: PropTypes.number,
    source: PropTypes.string,
};

export default RoadTripRender;
